import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { LanguageService, Product, ProductService, TranslatePipe, UrlPipe } from '@spartacus/core';
import {
  BREAKPOINT,
  BreakpointService,
  CmsComponentData,
  CurrentProductService,
  MediaComponent,
} from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { Observable, Subscription, combineLatest, concat, forkJoin, of } from 'rxjs';
import { delay, first, map, mergeMap, retryWhen, take, tap } from 'rxjs/operators';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { ShimanoProduct } from 'src/app/model/product.model';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { DealerPriceCheckerService } from 'src/app/utility/dealer-price-checker.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { SlickCarouselUtilService } from 'src/app/utility/slick-carousel.util';
import { RemoveImageTitleAttrDirective } from '../../../../shared/remove-title-attr.directive';

@Component({
  selector: 'app-product-search-empty',
  templateUrl: './product-search-empty.component.html',
  styleUrls: ['./product-search-empty.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    SlickCarouselModule,
    RouterLink,
    MediaComponent,
    RemoveImageTitleAttrDirective,
    MatIcon,
    AsyncPipe,
    TranslatePipe,
    UrlPipe,
  ],
})
export class ProductSearchEmptyComponent implements OnInit, OnDestroy {
  slickConfig: any;
  showSrp = false;
  srpSubscription$: Subscription;
  itemsSubscription$: Subscription;
  dealerPrices: DealerPrice[];
  carouselLocation: string;
  isMobile = false;
  isTablet = false;
  hasDealerPrice = true;
  dealerPriceCheckerSubscription$: Subscription;
  csDesktopWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  csTabletWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  /**
   * returns an Obervable string for the title
   */
  title$: Observable<string> = this.component.data$.pipe(map((d: any) => d.title));
  businessType: string;

  constructor(
    protected component: CmsComponentData<any>,
    protected productService: ProductService,
    protected current: CurrentProductService,
    private deviceService: DeviceDetectorService,
    private componentService: ComponentService,
    private dealerPriceService: DealerPriceService,
    private cdr: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    private gtmService: GoogleTagManagerService,
    private dealerPriceChecker: DealerPriceCheckerService,
    private currentBusinesstype: CurrentBusinessType,
    private languageService: LanguageService,
    private countryService: ShimanoCountryService,
    private storageService: StorageService
  ) {
    const slickConfig = new SlickCarouselUtilService();
    const slickObj = slickConfig.defaultConfig;
    slickObj.slidesToShow = 4;
    slickObj.slidesToScroll = 4;
    if (!this.deviceService.isDesktop()) {
      slickObj.arrows = false;
    }
    this.slickConfig = slickObj;
  }

  items$: Observable<Observable<ShimanoProduct>[]> = this.component.data$.pipe(
    first(),
    tap((data) => {
      this.carouselLocation = data.name;
    }),
    map((data: any) => {
      if (typeof data.productCodes === 'undefined') {
        throw new Error();
      }
      return data.productCodes.trim().split(' ').splice(0, data.itemCount);
    }),
    retryWhen((errors) => errors.pipe(take(2), delay(1000))),
    mergeMap((productCodes) => {
      return forkJoin([of(productCodes), this.getDealerPrices(productCodes)]);
    }),
    map(([codes, dealerPrices]) => {
      this.dealerPrices = dealerPrices.dealerPrices;
      return codes.map((code: any) => this.productService.get(code));
    })
  );

  ngOnInit(): void {
    this.isMobile = window.innerWidth < this.csTabletWidth;
    this.isTablet = window.innerWidth < this.csDesktopWidth && window.innerWidth >= this.csTabletWidth;

    this.itemsSubscription$ = this.items$
      .pipe(
        map((items$) => {
          combineLatest(items$.map((item) => concat(item))).subscribe((products) => {
            this.gtmEmptySearchProductImpressions(products, products?.length);
          });
        })
      )
      .subscribe();

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getDealerPrices(productCodes: string[]): Observable<any> {
    return this.dealerPriceService.getDealerPricesByProductCodes(productCodes);
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string): number {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceValueByProductCode(this.dealerPrices, productCode);
    }
  }

  getImageRendition(image: any) {
    let size: string;
    if (this.isMobile || this.isTablet) {
      size = ShimanoUtilMethod.imageRendition(177);
    } else {
      size = ShimanoUtilMethod.imageRendition(240);
    }
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  gtmEmptySearchProductImpressions(products: any[], count: number) {
    const impressionsList: any[] = [];
    products.forEach((product, index) => {
      if (product) {
        impressionsList.push({
          item_id: product.code,
          item_name: product.name,
          affiliation: this.affiliation,
          index: index,
          item_brand: product?.brand?.name,
          price: this.dealerPriceValue(product.code),
          item_list_name: this.carouselLocation,
        });
      }
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_IMPRESSION,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        items: impressionsList,
      },
    };
    if (count === gtmTag.ecommerce.items.length) {
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTag);
    }
  }

  gtmProductClick(product: Product, position: number) {
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_CLICK,
      ecommerce: '',
      content_type: 'product',
      content_id: product.code,
      content_name: product.name,
    };

    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.itemsSubscription$) {
      this.itemsSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
  }
}
